import React from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    IconButton,
    Typography,
    Select,
    MenuItem,
    OutlinedInput,
    InputLabel,
    Button,
    Box,
    FormControl
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { IAddUpdatePageSectionRequest } from "../../../../model/admin-portal/request/add-update-custom-page-request";
import { RichTextEditor } from "../../../components/rich-text-editor";
import DefaultUploadFile from "../../../components/default-upload-file";
import { ItemForm } from "./item-form";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';

interface SecaoFormProps {
    id: string;
    section: IAddUpdatePageSectionRequest;
    sectionIndex: number;
    onUpdate: (sectionIndex: number, field: string, value: any) => void;
    onRemove: (sectionIndex: number) => void;
    onAddItem: (sectionIndex: number) => void;
    onRemoveItem: (sectionIndex: number, itemIndex: number) => void;
    onUpdateItem: (sectionIndex: number, itemIndex: number, field: string, value: any) => void;
    getFileForSection: (sectionIndex: number) => File | null;
    setFileForSection: (sectionIndex: number, file: File | null) => void;
    getFileForItem: (sectionIndex: number, itemIndex: number) => File | null;
    setFileForItem: (sectionIndex: number, itemIndex: number, file: File | null) => void;
}

const SECTION_TYPES = [
    { value: 1, label: 'Texto Livre' },
    { value: 2, label: 'Lista de Itens' },
    { value: 3, label: 'Galeria de Imagens' },
    { value: 4, label: 'Acordeão/FAQ' },
    { value: 5, label: 'Conteúdo Misto' },
    { value: 6, label: 'Vídeo Embed' }
];

export const SecaoForm: React.FC<SecaoFormProps> = ({
    id,
    section,
    sectionIndex,
    onUpdate,
    onRemove,
    onAddItem,
    onRemoveItem,
    onUpdateItem,
    getFileForSection,
    setFileForSection,
    getFileForItem,
    setFileForItem
}) => {
    const [expandedItemIndex, setExpandedItemIndex] = React.useState<string | null>(null);
    const sectionTypeName = SECTION_TYPES.find(t => t.value === section.type)?.label || 'Nova Seção';

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleItemDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const items = section.items || [];
            const oldIndex = items.findIndex((_, idx) => `item-${sectionIndex}-${idx}` === active.id);
            const newIndex = items.findIndex((_, idx) => `item-${sectionIndex}-${idx}` === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const result = Array.from(items);
                const [removed] = result.splice(oldIndex, 1);
                result.splice(newIndex, 0, removed);

                const updatedItems = result.map((item, index) => ({
                    ...item,
                    order: index + 1
                }));

                // Update section with reordered items
                const updatedSection = {
                    ...section,
                    items: updatedItems
                };

                // Call parent update through the existing onUpdate mechanism
                onUpdate(sectionIndex, 'items', updatedItems);
            }
        }
    };

    return (
        <Accordion
            ref={setNodeRef}
            style={style}
            defaultExpanded
            sx={{
                mb: 2,
                border: isDragging ? '2px dashed #1976d2' : 'none'
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    backgroundColor: '#f5f5f5',
                    '&:hover': { backgroundColor: '#eeeeee' }
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box
                            {...attributes}
                            {...listeners}
                            sx={{
                                cursor: 'grab',
                                display: 'flex',
                                alignItems: 'center',
                                '&:active': { cursor: 'grabbing' }
                            }}
                        >
                            <DragIndicatorIcon color="action" />
                        </Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Seção {sectionIndex + 1}: {section.title || sectionTypeName}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove(sectionIndex);
                        }}
                        color="error"
                        sx={{ mr: 1 }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                <Grid container spacing={2} sx={{ pt: 2 }}>
                    {/* Section Type */}
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tipo de Seção *</InputLabel>
                            <Select
                                value={section.type || 1}
                                onChange={(e) => onUpdate(sectionIndex, 'type', e.target.value)}
                                label="Tipo de Seção *"
                            >
                                {SECTION_TYPES.map(type => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Title */}
                    <Grid item xs={12}>
                        <InputLabel>Título da Seção</InputLabel>
                        <OutlinedInput
                            size="small"
                            value={section.title || ''}
                            onChange={(e) => onUpdate(sectionIndex, 'title', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Content (Type 1 only) */}
                    {section.type === 1 && (
                        <Grid item xs={12}>
                            <InputLabel>Conteúdo HTML *</InputLabel>
                            <RichTextEditor
                                value={section.content || ''}
                                onChange={(value) => onUpdate(sectionIndex, 'content', value)}
                                placeholder="Digite o conteúdo da seção..."
                                error={!section.content || section.content.length === 0}
                            />
                            {(!section.content || section.content.length === 0) && (
                                <Typography variant="caption" color="error">
                                    Conteúdo é obrigatório para seções do tipo "Texto Livre"
                                </Typography>
                            )}
                        </Grid>
                    )}

                    {/* URL (Type 6 only) */}
                    {section.type === 6 && (
                        <Grid item xs={12}>
                            <InputLabel>URL do Vídeo *</InputLabel>
                            <OutlinedInput
                                size="small"
                                value={section.url || ''}
                                onChange={(e) => onUpdate(sectionIndex, 'url', e.target.value)}
                                fullWidth
                                placeholder="https://youtube.com/watch?v=..."
                                error={!section.url || section.url.length === 0}
                            />
                            {(!section.url || section.url.length === 0) && (
                                <Typography variant="caption" color="error">
                                    URL é obrigatória para seções do tipo "Vídeo Embed"
                                </Typography>
                            )}
                        </Grid>
                    )}

                    {/* Image Upload (all types) */}
                    <Grid item xs={12}>
                        <InputLabel>Imagem da Seção</InputLabel>
                        <DefaultUploadFile
                            id={`section-${sectionIndex}-image`}
                            file={getFileForSection(sectionIndex) ?? undefined}
                            setFileUpload={(file) => setFileForSection(sectionIndex, file)}
                            extensions={['jpg', 'jpeg', 'png', 'gif']}
                            maxFileSizeMb={5}
                        />
                    </Grid>

                    {/* Items Section (Types 2-5) */}
                    {[2, 3, 4, 5].includes(section.type) && (
                        <Grid item xs={12}>
                            <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Itens da Seção
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        const newItemIndex = section.items?.length || 0;
                                        onAddItem(sectionIndex);
                                        setExpandedItemIndex(`${sectionIndex}-${newItemIndex}`);
                                    }}
                                >
                                    Adicionar Item
                                </Button>
                            </Box>

                            {section.items && section.items.length > 0 ? (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleItemDragEnd}
                                >
                                    <SortableContext
                                        items={section.items.map((_, idx) => `item-${sectionIndex}-${idx}`)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {section.items.map((item, itemIndex) => (
                                            <ItemForm
                                                key={`item-${sectionIndex}-${itemIndex}`}
                                                id={`item-${sectionIndex}-${itemIndex}`}
                                                item={item}
                                                sectionIndex={sectionIndex}
                                                itemIndex={itemIndex}
                                                onUpdate={onUpdateItem}
                                                onRemove={onRemoveItem}
                                                getFile={getFileForItem}
                                                setFile={setFileForItem}
                                                expanded={expandedItemIndex === `${sectionIndex}-${itemIndex}`}
                                                onExpandChange={(secIdx, itmIdx, isExpanded) => {
                                                    setExpandedItemIndex(isExpanded ? `${secIdx}-${itmIdx}` : null);
                                                }}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            ) : (
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                                    Nenhum item adicionado ainda. Clique em "Adicionar Item" para começar.
                                </Typography>
                            )}
                        </Grid>
                    )}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};
